import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../components/ui/accordion';
import { Header } from '../components/Header';
import { ReturningProspectBanner } from '../components/ReturningProspectBanner';
import { AvailabilityCalendar } from '../components/HeroAvailabilityCalendar';
import { ConfettiCanvas } from '../components/ConfettiCanvas';
import { HeroCelebrationVariant } from '../components/HeroCelebrationVariant';
import { HeroFilmStripSlides } from '../components/HeroFilmStripSlides';
import { HeroSplit } from '../components/heros/HeroSplit';
import { HeroMascot } from '../components/heros/HeroMascot';
import { HeroVideo } from '../components/heros/HeroVideo';
import { HeroMinimalist } from '../components/heros/HeroMinimalist';
import { HeroStoryCards } from '../components/heros/HeroStoryCards';
import { resolveFontFamily } from '../lib/fonts';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  ArrowRight, Palette, Shirt, Cake, Sparkles, Gift, Grape,
  Star, CheckCircle2, Phone, Mail, MapPin, CalendarDays,
  ChevronLeft, ChevronRight, Heart, PartyPopper, Cookie, IceCream,
  Users, Clock,
} from 'lucide-react';
import { useCmsContent, subscribeCmsUpdate } from '../hooks/useCmsContent';

// Lookup table so admins can pick an icon by name in the CMS editor.
const ICON_MAP = {
  Grape, Palette, Sparkles, Cake, Shirt, Gift, Heart, PartyPopper, Cookie, IceCream,
};
const resolveIcon = (name) => ICON_MAP[name] || Sparkles;

// Convert /api/uploads/... to absolute URL. CMS image fields can hold either.
const _API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const cmsUrl = (u) => {
  if (!u) return '';
  if (/^https?:\/\//.test(u)) return u;
  if (u.startsWith('/api/')) return `${_API_BASE}${u}`;
  return u;
};

const BRAND = {
  pink: '#FF3B6B',
  orange: '#FF7A2E',
  teal: '#06D6A0',
  yellow: '#FFD166',
  indigo: '#6366F1',
  ink: '#1E1E24',
  cream: '#FFFDF8',
};

// ---------- Section 1 · Hero ----------
const HERO_RIBBON_FALLBACK = [
  { emoji: '🍉', label: 'DIY Fruit Bouquets' },
  { emoji: '🎨', label: 'Art & Crafts' },
  { emoji: '📿', label: 'Bracelets & Keychains' },
  { emoji: '🎂', label: 'Custom Cakes' },
  { emoji: '👕', label: 'Print Your Own Gift' },
];

// Reusable component for the 5 "experience" capsule pills inside the hero.
// Position (above/below CTAs / hidden) and style (pills vs text) are driven
// by CMS settings on the hero section (`experience_pills_position` /
// `experience_pills_style`). Items themselves are CMS-editable via iter-107
// (`experience_pills_items`); when empty we fall back to the bundled list.
const HeroExperiencePills = ({ items, style = 'pills', delay = 0.35, className = '' }) => {
  const list = Array.isArray(items) && items.length > 0 ? items : HERO_RIBBON_FALLBACK;
  return (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ delay }}
    className={`flex flex-wrap gap-x-3 gap-y-2 ${className}`}
    data-testid="hero-ribbon"
    data-style={style}
  >
    {list.map((r, idx) => (
      style === 'text' ? (
        <span
          key={r.id || `${r.label}-${idx}`}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-[#1E1E24]"
        >
          <span className="text-base" aria-hidden>{r.emoji}</span>
          <span className="underline-offset-4 hover:underline">{r.label}</span>
        </span>
      ) : (
        <span
          key={r.id || `${r.label}-${idx}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#1E1E24]/10 text-sm font-medium shadow-[0_2px_0_0_#1E1E2410]"
        >
          <span className="text-base" aria-hidden>{r.emoji}</span>
          <span className="text-[#1E1E24]">{r.label}</span>
        </span>
      )
    ))}
  </motion.div>
  );
};

const HERO_FLOATERS = [
  { emoji: '🍓', x: '6%',  y: '18%', delay: 0 },
  { emoji: '🎨', x: '12%', y: '68%', delay: 0.3 },
  { emoji: '🧁', x: '85%', y: '22%', delay: 0.6 },
  { emoji: '🎈', x: '88%', y: '72%', delay: 0.9 },
  { emoji: '✨', x: '45%', y: '10%', delay: 0.15 },
  { emoji: '📿', x: '72%', y: '48%', delay: 0.45 },
  { emoji: '🍇', x: '22%', y: '48%', delay: 0.75 },
];

const DEFAULT_HERO_SLIDES = [
  {
    child_name: 'Emma', age: 7,
    message: "The DIY fruit bouquet station was the hit of the party — Emma still talks about it two weeks later!",
    signature: 'Mom of Emma',
    image: 'https://images.unsplash.com/photo-1558636508-e0db3814bd1d?auto=format&fit=crop&w=900&q=80',
  },
  {
    child_name: 'Mia', age: 6,
    message: "Mia wore her bracelet every day for a week. The craft stations kept all 12 kids happily busy.",
    signature: 'Mom of Mia',
    image: 'https://images.unsplash.com/photo-1464349095431-e9a21285b5f3?auto=format&fit=crop&w=900&q=80',
  },
  {
    child_name: 'Leo', age: 9,
    message: "Leo printed his own T-shirt with a dinosaur he drew. Best souvenir a 9-year-old could ask for.",
    signature: 'Dad of Leo',
    image: 'https://images.unsplash.com/photo-1607453998774-d533f65dac99?auto=format&fit=crop&w=900&q=80',
  },
  {
    child_name: 'Chloe', age: 8,
    message: "The custom unicorn cake was photo-worthy AND delicious. Our family is hooked!",
    signature: 'Mom of Chloe',
    image: 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?auto=format&fit=crop&w=900&q=80',
  },
];

// Sticky-note testimonial carousel with a 3D page-flip transition
const HeroCarousel = () => {
  const [slides, setSlides] = useState(DEFAULT_HERO_SLIDES);
  const [active, setActive] = useState(0);
  const [dir, setDir] = useState(1); // 1 = next, -1 = prev

  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API}/api/public/hero-testimonials`)
      .then((r) => r.json())
      .then((d) => {
        if (Array.isArray(d?.testimonials) && d.testimonials.length > 0) {
          // Map CMS fields → sticky-note fields (signature from customer_name if provided)
          const API_HOST = process.env.REACT_APP_BACKEND_URL;
          setSlides(d.testimonials.map((t) => ({
            child_name: t.child_name,
            age: t.age,
            message: t.message,
            signature: t.signature || (t.customer_name ? t.customer_name : `Parent of ${t.child_name}`),
            image: t.image && t.image.startsWith('/api/') ? `${API_HOST}${t.image}` : t.image,
            created_at: t.created_at,
          })));
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (slides.length < 2) return;
    const t = setInterval(() => {
      setDir(1);
      setActive((p) => (p + 1) % slides.length);
    }, 6000);
    return () => clearInterval(t);
  }, [slides.length]);

  const goTo = (idx) => {
    if (idx === active) return;
    setDir(idx > active ? 1 : -1);
    setActive(idx);
  };
  const prev = () => { setDir(-1); setActive((p) => (p - 1 + slides.length) % slides.length); };
  const next = () => { setDir(1);  setActive((p) => (p + 1) % slides.length); };

  const current = slides[active] || {};
  const suffix = current.age === 1 ? 'st' : current.age === 2 ? 'nd' : current.age === 3 ? 'rd' : 'th';
  // Slight alternating tilt per slide for a more authentic sticky-note feel
  const tilts = [-3, 2, -2, 3, -1];
  const tilt = tilts[active % tilts.length];
  // Polaroid-style caption: "{child_name}'s crew[ · Mon YYYY]"
  const photoCaption = (() => {
    if (!current.child_name) return '';
    const base = `${current.child_name}'s crew`;
    if (!current.created_at) return base;
    const d = new Date(current.created_at);
    if (isNaN(d.getTime())) return base;
    return `${base} · ${d.toLocaleString('en-US', { month: 'short', year: 'numeric' })}`;
  })();

  return (
    <div data-testid="hero-carousel" aria-label="Customer testimonials" className="w-full">
      <div className="sticky-note-stage">
        <AnimatePresence initial={false} mode="wait" custom={dir}>
          <motion.div
            key={active}
            custom={dir}
            initial={{ rotateY: dir * 90, opacity: 0 }}
            animate={{ rotateY: 0, opacity: 1, rotate: tilt }}
            exit={{ rotateY: dir * -90, opacity: 0 }}
            transition={{ duration: 0.55, ease: [0.4, 0, 0.2, 1] }}
            className="sticky-note"
            data-testid={`hero-slide-${active}`}
          >
            {current.image && (
              <div className="sn-photo-wrap">
                <img
                  src={current.image}
                  alt={`${current.child_name}'s birthday`}
                  className="testimonial-img"
                  loading="lazy"
                />
                {photoCaption && (
                  <span className="sn-photo-caption" data-testid="sn-photo-caption">{photoCaption}</span>
                )}
              </div>
            )}
            <div className="sn-stars" aria-hidden>★ ★ ★ ★ ★</div>
            <p className="sn-title" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              {current.child_name}'s {current.age}{suffix} Birthday
            </p>
            <p className="sn-message">
              "{current.message}"
            </p>
            {current.signature && (
              <p className="sn-signature">— {current.signature}</p>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Nav */}
      {slides.length > 1 && (
        <div className="mt-4 flex items-center justify-between" data-testid="hero-nav">
          <button
            type="button"
            onClick={prev}
            aria-label="Previous testimonial"
            className="sticky-note-nav-btn"
            data-testid="hero-prev"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-1.5" data-testid="hero-dots">
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Go to testimonial ${i + 1}`}
                onClick={() => goTo(i)}
                data-testid={`hero-dot-${i}`}
                className={`rounded-full transition-all ${i === active ? 'w-6 h-2 bg-[#FF3B6B]' : 'w-2 h-2 bg-[#1E1E24]/20 hover:bg-[#1E1E24]/40'}`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={next}
            aria-label="Next testimonial"
            className="sticky-note-nav-btn"
            data-testid="hero-next"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

const HeroSection = ({ onPrimary, onOpenAvailability, cms }) => {
  // CMS-supplied values override the hardcoded defaults; if a field is empty,
  // we fall back to the original copy so the page never renders blank.
  // ---- iter-104: optional background image takes precedence over gradient ----
  const bgImage = cms?.background_image ? cmsUrl(cms.background_image) : '';
  const overlayOpacity = Math.min(1, Math.max(0, Number(cms?.background_overlay_opacity ?? 0.35)));
  const gradient = (cms && (cms.gradient_start || cms.gradient_mid || cms.gradient_end))
    ? `linear-gradient(135deg, ${cms.gradient_start || '#FFDEE9'} 0%, ${cms.gradient_mid || '#FFF4D6'} 45%, ${cms.gradient_end || '#B5FFFC'} 100%)`
    : 'linear-gradient(135deg, #FFDEE9 0%, #FFF4D6 45%, #B5FFFC 100%)';
  const heroBg = bgImage
    ? `linear-gradient(rgba(0,0,0,${overlayOpacity}), rgba(0,0,0,${overlayOpacity})), url(${bgImage}) center/cover no-repeat`
    : gradient;
  const animatedWords = (cms && Array.isArray(cms.animated_words) && cms.animated_words.length > 0)
    ? cms.animated_words
    : ['CREATE.', 'PLAY.', 'CELEBRATE.'];
  const defaultWordColors = [
    [BRAND.pink, BRAND.orange, BRAND.pink],
    [BRAND.teal, BRAND.indigo, BRAND.teal],
    [BRAND.orange, BRAND.yellow, BRAND.orange],
    [BRAND.indigo, BRAND.pink, BRAND.indigo],
    [BRAND.yellow, BRAND.orange, BRAND.yellow],
  ];
  // iter-104: per-word custom colors override the brand 5-color rotation.
  const customWordColors = Array.isArray(cms?.headline_word_colors) ? cms.headline_word_colors : [];
  const wordColorAt = (idx) => {
    const c = customWordColors[idx];
    return (typeof c === 'string' && c.trim()) ? [c, c, c] : defaultWordColors[idx % defaultWordColors.length];
  };
  // iter-104: headline typography overrides
  const headlineFontFamily = resolveFontFamily(cms?.headline_font_family);
  const headlineFontSize = Number(cms?.headline_font_size_px) > 0
    ? `clamp(2.25rem, 6vw, ${Number(cms.headline_font_size_px)}px)`
    : undefined;
  const headlineAlignment = (cms?.headline_alignment === 'center' || cms?.headline_alignment === 'right')
    ? cms.headline_alignment : 'left';
  const headlineTextAlignClass = headlineAlignment === 'center' ? 'text-center'
                                : headlineAlignment === 'right' ? 'text-right' : 'text-left';
  // iter-104: confetti toggle (default ON for backward-compat)
  const confettiOn = cms?.confetti_enabled !== false;

  // ---- iter-105: sub-headline + trust-badges typography ----
  const subFontFamily   = resolveFontFamily(cms?.subheadline_font_family);
  const subFontSize     = Number(cms?.subheadline_font_size_px) > 0
    ? `clamp(14px, 2.2vw, ${Number(cms.subheadline_font_size_px)}px)` : undefined;
  const subColor        = cms?.subheadline_color || '#1E1E24';
  const subAlign        = (cms?.subheadline_alignment === 'center' || cms?.subheadline_alignment === 'right')
                          ? cms.subheadline_alignment : 'left';
  const subAlignClass   = subAlign === 'center' ? 'text-center' : subAlign === 'right' ? 'text-right' : 'text-left';
  const subJustifyClass = subAlign === 'center' ? 'justify-center' : subAlign === 'right' ? 'justify-end' : 'justify-start';

  const trustFontFamily = resolveFontFamily(cms?.trust_badges_font_family);
  const trustFontSize   = Number(cms?.trust_badges_font_size_px) > 0
    ? `${Number(cms.trust_badges_font_size_px)}px` : undefined;
  const trustColor      = cms?.trust_badges_color || '';
  const trustAlign      = (cms?.trust_badges_alignment === 'center' || cms?.trust_badges_alignment === 'right')
                          ? cms.trust_badges_alignment : 'left';
  const trustJustifyClass = trustAlign === 'center' ? 'justify-center' : trustAlign === 'right' ? 'justify-end' : 'justify-start';

  // ---- iter-105: experience pills (5 capsules) position + style ----
  const pillsPosition = ['above_ctas', 'below_ctas', 'hidden'].includes(cms?.experience_pills_position)
    ? cms.experience_pills_position : 'above_ctas';
  const pillsStyle    = cms?.experience_pills_style === 'text' ? 'text' : 'pills';
  // iter-107 — admin-editable capsule items. Empty list = fallback.
  const pillsItems    = Array.isArray(cms?.experience_pills_items) ? cms.experience_pills_items : null;

  const headlineEmoji = cms?.headline_emoji ?? '🎉';
  const badgeText = cms?.badge_text || 'Creative Birthdays · Oakville';
  const subheadline = cms?.subheadline || 'More than a party – a hands-on adventure. DIY fruit bouquets, craft stations, order custom cakes, and take home the gifts the kids design themselves.';
  const locationLine = cms?.location_line || 'We are located inside Edible Arrangements Oakville';
  const primaryCta = cms?.primary_cta_text || 'Build Your Dream Party';
  const secondaryCta = cms?.secondary_cta_text || 'Check Availability';
  const trustBadges = (cms && Array.isArray(cms.trust_badges) && cms.trust_badges.length > 0)
    ? cms.trust_badges
    : ['Allergen-Safe Options', 'Flexible Packages', 'Free Cancellation up to 24h'];
  return (
  <section
    className="relative overflow-hidden min-h-[92vh] flex items-center"
    style={{ background: heroBg }}
    data-testid="landing-hero"
  >
    {/* Continuous confetti rain behind the hero content (toggleable). */}
    {confettiOn && <ConfettiCanvas count={150} />}

    {/* Floating decorative emoji */}
    {HERO_FLOATERS.map((f, i) => (
      <motion.span
        key={i}
        className="absolute select-none text-4xl sm:text-5xl pointer-events-none"
        style={{ left: f.x, top: f.y }}
        initial={{ y: 0, rotate: 0, opacity: 0.0 }}
        animate={{ y: [0, -14, 0], rotate: [0, 4, -4, 0], opacity: 0.9 }}
        transition={{ duration: 5 + i * 0.4, delay: f.delay, repeat: Infinity, ease: 'easeInOut' }}
        aria-hidden
      >
        {f.emoji}
      </motion.span>
    ))}

    {/* Confetti dots */}
    <div className="absolute inset-0 pointer-events-none" aria-hidden>
      {Array.from({ length: 24 }).map((_, i) => (
        <span
          key={i}
          className="absolute rounded-full"
          style={{
            width: `${4 + (i % 4) * 2}px`,
            height: `${4 + (i % 4) * 2}px`,
            left: `${(i * 37) % 100}%`,
            top: `${(i * 53) % 100}%`,
            backgroundColor: [BRAND.pink, BRAND.teal, BRAND.yellow, BRAND.indigo, BRAND.orange][i % 5],
            opacity: 0.55,
          }}
        />
      ))}
    </div>

    <div className="relative z-10 container mx-auto px-4 pt-4 pb-10 sm:pt-8 sm:pb-14 grid lg:grid-cols-[1.5fr_1fr] gap-10 items-center">
      {/* Left: copy */}
      <div>
        <motion.p
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/80 backdrop-blur-sm border border-white shadow-sm text-sm text-[#1E1E24] font-medium mb-6"
        >
          <Sparkles className="w-4 h-4" style={{ color: BRAND.pink }} />
          {badgeText}
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
          className={`text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] mb-5 ${headlineTextAlignClass}`}
          style={{
            fontFamily: headlineFontFamily,
            ...(headlineFontSize ? { fontSize: headlineFontSize } : {}),
          }}
          data-testid="hero-flash-headline"
        >
          {animatedWords.map((word, idx) => (
            <React.Fragment key={`${word}-${idx}`}>
              <motion.span
                animate={{ color: wordColorAt(idx) }}
                transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut', delay: idx * 0.4 }}
              >{word}</motion.span>{' '}
            </React.Fragment>
          ))}
          <motion.span
            className="inline-block"
            animate={{ rotate: [0, 12, -8, 0], scale: [1, 1.15, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          >{headlineEmoji}</motion.span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
          className={`mb-3 max-w-xl text-[#1E1E24]/80 ${subAlignClass}`}
          style={{
            fontFamily: subFontFamily,
            color: subColor,
            ...(subFontSize ? { fontSize: subFontSize } : {}),
          }}
          data-testid="hero-subtext"
        >
          {subheadline}
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.32 }}
          className={`inline-flex items-center gap-2 text-sm font-medium text-[#1E1E24] bg-white/70 backdrop-blur-sm border border-white rounded-full px-3.5 py-1.5 mb-7 ${subAlign !== 'left' ? `flex ${subJustifyClass}` : ''}`}
          data-testid="hero-location-line"
        >
          <MapPin className="w-3.5 h-3.5" style={{ color: BRAND.pink }} />
          {locationLine}
        </motion.p>

        {/* Offer ribbon (experience pills) — only render when position == above_ctas */}
        {pillsPosition === 'above_ctas' && (
          <HeroExperiencePills items={pillsItems} style={pillsStyle} delay={0.35} className="mb-8" />
        )}

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}
          className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6"
        >
          <Button
            size="lg"
            onClick={onPrimary}
            className="group rounded-full text-white px-7 h-14 text-base font-semibold shadow-[0_8px_30px_-10px_rgba(255,59,107,0.55)] hover:shadow-[0_14px_40px_-10px_rgba(255,59,107,0.65)] transition-all"
            style={{ background: `linear-gradient(135deg, ${BRAND.pink} 0%, ${BRAND.orange} 100%)` }}
            data-testid="hero-build-party-btn"
          >
            <span className="mr-2" aria-hidden>🎈</span>
            {primaryCta}
            <ArrowRight className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
          </Button>
          <Button
            size="lg"
            variant="outline"
            onClick={onOpenAvailability}
            className="rounded-full h-14 px-6 text-base font-semibold bg-white border-[#E8E5DF] text-[#1E1E24] hover:bg-white hover:border-[#FF3B6B] hover:text-[#FF3B6B] transition-all"
            data-testid="hero-check-availability-btn"
          >
            <CalendarDays className="w-5 h-5 mr-2" />
            {secondaryCta}
          </Button>
        </motion.div>

        {/* Pills below the CTAs (iter-105) */}
        {pillsPosition === 'below_ctas' && (
          <HeroExperiencePills items={pillsItems} style={pillsStyle} delay={0.5} className="mb-6" />
        )}

        {/* Trust badges */}
        <motion.ul
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55 }}
          className={`flex flex-wrap gap-x-5 gap-y-2 ${trustJustifyClass}`}
          style={{
            fontFamily: trustFontFamily,
            fontSize: trustFontSize || undefined,
            color: trustColor || 'rgba(30,30,36,0.75)',
          }}
          data-testid="hero-trust-badges"
        >
          {trustBadges.map((t) => (
            <li key={t} className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4" style={{ color: BRAND.teal }} />
              {t}
            </li>
          ))}
        </motion.ul>
      </div>

      {/* Right: testimonial carousel */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }}
        className="relative hidden lg:block max-w-[420px] ml-auto w-full"
      >
        <div className="testimonial-frame" data-testid="testimonial-frame">
          <HeroCarousel />
        </div>
      </motion.div>
    </div>
  </section>
  );
};

// ---------- Section 2 · Unique Experiences ----------

// Default fallback list (also used if CMS returns empty). Kept in sync with
// CMS_DEFAULTS["experiences"] in backend so the page never renders blank.
const DEFAULT_EXPERIENCES = [
  { id: 'diy-fruit-bouquet',        icon: 'Grape',    color: '#FF3B6B', bg: '#FFE5EC', title: 'DIY Fruit Bouquet',       desc: 'Pick, skewer, arrange — edible art kids eat right after.',     href: '/unique-experiences/diy-fruit-bouquet',        enabled: true, sort_order: 1 },
  { id: 'art-and-craft-corner',     icon: 'Palette',  color: '#6366F1', bg: '#EBECFE', title: 'Art & Craft Corner',      desc: 'Paint, glitter & stickers — every masterpiece comes home.',     href: '/unique-experiences/art-and-craft-corner',     enabled: true, sort_order: 2 },
  { id: 'bracelet-and-keychain-bar',icon: 'Sparkles', color: '#E8A80E', bg: '#FFF3CD', title: 'Bracelet & Keychain Bar', desc: 'Beads + charms — wearable memories to take home.',              href: '/unique-experiences/bracelet-and-keychain-bar',enabled: true, sort_order: 3 },
  { id: 'custom-cake-studio',       icon: 'Cake',     color: '#06D6A0', bg: '#D8F5EA', title: 'Custom Cake Studio',      desc: 'Flavour, filling, theme — baked fresh for the party.',          href: '/unique-experiences/custom-cake-studio',       enabled: true, sort_order: 4 },
  { id: 'print-your-own-gift',      icon: 'Shirt',    color: '#FB923C', bg: '#FFE0C9', title: 'Print Your Own Gift',     desc: 'Design + print on caps, tees, mugs on the spot.',               href: '/unique-experiences/print-your-own-gift',      enabled: true, sort_order: 5 },
  { id: 'custom-package-builder',   icon: 'Gift',     color: '#EC4899', bg: '#FBE0F1', title: 'Custom Package Builder',  desc: 'Mix activities, food & decor — we handle the details.',         href: '/unique-experiences/custom-package-builder',   enabled: true, sort_order: 6 },
];

const ExperiencesSection = ({ onLearnMore, cms }) => {
  const items = (cms?.items?.length ? cms.items : DEFAULT_EXPERIENCES)
    .filter((e) => e.enabled !== false)
    .slice()
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  const eyebrow = cms?.eyebrow || 'UNIQUE EXPERIENCES';
  const title   = cms?.title   || 'Six things your kids will talk about for weeks';
  const subtitle= cms?.subtitle|| 'Hand-picked stations that turn a birthday into a memory.';
  return (
  <section className="py-10 sm:py-12 fade-up bg-white">
    <div className="container mx-auto px-4">
      <div className="max-w-2xl mb-8">
        <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: `${BRAND.indigo}15`, color: BRAND.indigo }}>
          {eyebrow}
        </span>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold mt-3 mb-2 text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
          {title}
        </h2>
        <p className="text-[#6B6B7A] text-sm sm:text-base">
          {subtitle}
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
        {items.map((e, i) => {
          const Icon = resolveIcon(e.icon);
          return (
            <motion.button
              type="button"
              key={e.id || e.title}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              viewport={{ once: true, margin: '-40px' }}
              whileHover={{ y: -4, scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onLearnMore(e)}
              data-testid={`experience-${(e.title || '').toLowerCase().replace(/\s+/g, '-')}`}
              className="group relative rounded-2xl p-4 sm:p-5 text-left cursor-pointer transition-shadow min-h-[150px] flex flex-col items-start overflow-hidden"
              style={{ backgroundColor: e.bg, boxShadow: '0 2px 12px -6px rgba(30,30,36,0.08)' }}
              onMouseEnter={(ev) => { ev.currentTarget.style.boxShadow = `0 14px 30px -10px ${e.color}55`; }}
              onMouseLeave={(ev) => { ev.currentTarget.style.boxShadow = '0 2px 12px -6px rgba(30,30,36,0.08)'; }}
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 bg-white/80 group-hover:scale-110 group-hover:rotate-[-6deg] transition-transform"
              >
                <Icon className="w-5 h-5" style={{ color: e.color }} />
              </div>
              <h3 className="text-sm sm:text-[15px] font-semibold text-[#1E1E24] leading-tight mb-1" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                {e.title}
              </h3>
              <p className="text-[11px] sm:text-xs text-[#6B6B7A] leading-snug line-clamp-2">{e.desc}</p>
              <span
                className="mt-auto pt-2 text-[11px] font-semibold inline-flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ color: e.color }}
                data-testid={`experience-learn-${(e.title || '').toLowerCase().replace(/\s+/g, '-')}`}
              >
                Learn more <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-1" />
              </span>
            </motion.button>
          );
        })}
      </div>
    </div>
  </section>
  );
};

// ---------- Hero Highlight Banner ----------
const fmtPromoDate = (d) => {
  try { return new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }
  catch { return d; }
};

const HeroHighlightBanner = () => {
  const [rate, setRate] = useState(null);
  useEffect(() => {
    let on = true;
    fetch(`${_API_BASE}/api/public/loyalty/active-rate`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (on) setRate(d); })
      .catch(() => {});
    return () => { on = false; };
  }, []);
  const ratePts = rate?.points_per_dollar ?? 2;
  const promo = rate?.source === 'promo' ? rate?.promo : null;
  const ptsLabel = Number.isInteger(ratePts) ? ratePts : ratePts;

  return (
  <section
    className="relative -mt-6 sm:-mt-10 z-20 px-4 pb-2"
    data-testid="hero-highlight-banner"
    aria-label="Customer perks"
  >
    <div className="container mx-auto">
      {promo && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-3 rounded-2xl px-4 py-3 text-center text-white font-semibold text-sm sm:text-base shadow-lg"
          style={{ background: 'linear-gradient(135deg, #FF3B6B 0%, #FB923C 100%)' }}
          data-testid="loyalty-promo-banner"
        >
          🎉 {promo.name}! Earn {ptsLabel} points per $1
          {promo.end_date ? ` until ${fmtPromoDate(promo.end_date)}` : ''}.
        </motion.div>
      )}
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.5 }}
        transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
        className="relative rounded-3xl px-4 py-4 sm:px-6 sm:py-5 backdrop-blur-md"
        style={{
          background:
            'linear-gradient(135deg, rgba(255,255,255,0.55) 0%, rgba(255,236,242,0.55) 50%, rgba(225,239,255,0.55) 100%)',
          border: '1px solid rgba(255,255,255,0.7)',
          boxShadow: '0 18px 40px -20px rgba(255,59,107,0.25), inset 0 1px 0 rgba(255,255,255,0.6)',
        }}
      >
        {/* Subtle floating decoration */}
        <span aria-hidden className="hidden sm:block absolute -top-3 -left-2 text-2xl opacity-70">🎉</span>
        <span aria-hidden className="hidden sm:block absolute -top-2 -right-3 text-2xl opacity-60">✨</span>

        <p className="text-center text-[11px] sm:text-xs font-semibold uppercase tracking-[0.18em] text-[#FF3B6B] mb-3">
          Members get more
        </p>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4">
          <Link
            to="/loyalty-program"
            data-testid="banner-loyalty"
            className="group flex-1 max-w-md mx-auto sm:mx-0 inline-flex items-center gap-3 bg-white rounded-full pl-3 pr-5 py-2.5 shadow-sm hover:shadow-md transition-all border border-[#E8E5DF] hover:border-[#FF3B6B]/40"
          >
            <span
              className="flex-none w-10 h-10 rounded-full flex items-center justify-center text-white"
              style={{ background: 'linear-gradient(135deg, #FF3B6B 0%, #FB923C 100%)' }}
            >
              <Sparkles className="w-5 h-5" />
            </span>
            <span className="min-w-0 text-left">
              <span className="block text-sm font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }} data-testid="banner-loyalty-rate">
                Earn {ptsLabel} pt{ptsLabel === 1 ? '' : 's'} for every $1
              </span>
              <span className="block text-[11px] text-[#6B6B7A] truncate">
                Redeem for discounts &amp; freebies on your next party.
              </span>
            </span>
          </Link>

          <Link
            to="/birthday-invites"
            data-testid="banner-invites"
            className="group flex-1 max-w-md mx-auto sm:mx-0 inline-flex items-center gap-3 bg-white rounded-full pl-3 pr-5 py-2.5 shadow-sm hover:shadow-md transition-all border border-[#E8E5DF] hover:border-[#6366F1]/40"
          >
            <span
              className="flex-none w-10 h-10 rounded-full flex items-center justify-center text-white"
              style={{ background: 'linear-gradient(135deg, #6366F1 0%, #06D6A0 100%)' }}
            >
              <Mail className="w-5 h-5" />
            </span>
            <span className="min-w-0 text-left">
              <span className="block text-sm font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>
                Free digital invites with RSVP tracking
              </span>
              <span className="block text-[11px] text-[#6B6B7A] truncate">
                Send custom invites from your account in 2 minutes.
              </span>
            </span>
          </Link>
        </div>
      </motion.div>
    </div>
  </section>
  );
};

// ---------- Section 3 · How It Works ----------
const DEFAULT_STEPS = [
  { n: '01', title: 'Choose Your Adventure', desc: 'Pick a ready-made package or build your own from our stations.' },
  { n: '02', title: 'Add the Extras',        desc: 'Fruit bouquet, craft stations, custom cake, print-your-gift — stack them.' },
  { n: '03', title: 'We Prep Everything',    desc: 'We arrive, set up, host the activities and keep things moving.' },
  { n: '04', title: 'Take Memories Home',    desc: 'Kids leave with the gift they made, the edible they built, and the best story.' },
];

const HowItWorksSection = ({ cms }) => {
  const steps = cms?.steps?.length ? cms.steps : DEFAULT_STEPS;
  const eyebrow = cms?.eyebrow || 'HOW IT WORKS';
  const title = cms?.title || 'Four steps. Zero stress.';
  const bg = cms?.background_color || '#FFF8E7';
  return (
  <section className="py-10 sm:py-14 fade-up" style={{ backgroundColor: bg }}>
    <div className="container mx-auto px-4">
      <div className="max-w-2xl mb-14">
        <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: `${BRAND.orange}20`, color: BRAND.orange }}>
          {eyebrow}
        </span>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold mt-4 text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
          {title}
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {steps.map((s, i) => (
          <motion.div
            key={s.n || `step-${i}`}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.08 }}
            viewport={{ once: true }}
            className="bg-white rounded-3xl p-7 border border-[#1E1E24]/5 shadow-sm"
          >
            <p className="text-5xl font-bold mb-4" style={{ fontFamily: 'Fredoka, sans-serif', color: BRAND.pink }}>
              {s.n || String(i + 1).padStart(2, '0')}
            </p>
            <h3 className="text-xl font-semibold text-[#1E1E24] mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              {s.title}
            </h3>
            <p className="text-[#6B6B7A] text-sm leading-relaxed">{s.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
  );
};

// ---------- Section 4 · Packages ----------
// Per-package visual styling (color, emoji, tagline). Merged with the live
// `/api/packages` response on render so admin edits to name/price/features/
// active flag flow through to this Landing section without requiring a code
// change. New admin-created packages fall back to a sensible default style.
const PACKAGE_STYLES = {
  create:    { emoji: '🎨', color: BRAND.pink,  accent: '#FFF0F3', glow: 'rgba(255, 59, 107, 0.35)', cardBg: '#E6F0FA', tagline: 'The starter kit.' },
  celebrate: { emoji: '🎉', color: BRAND.teal,  accent: '#EDFFF8', glow: 'rgba(6, 214, 160, 0.35)',  cardBg: '#FFF0E0', tagline: 'The crowd favourite.', featured: true },
  signature: { emoji: '🌟', color: '#A855F7',   accent: '#FAF0FF', glow: 'rgba(168, 85, 247, 0.4)',  cardBg: '#F3E5F5', tagline: 'The blow-their-mind option.' },
};
const DEFAULT_STYLE = { emoji: '✨', color: BRAND.pink, accent: '#FFF0F3', glow: 'rgba(255, 59, 107, 0.35)', cardBg: '#FFF6F8', tagline: '' };

// Pick the SINGLE best automatically-applied special offer for a package.
// Mirrors `_resolve_auto_offer` on the backend so the customer sees the same
// number that ends up on the booking row.
const bestOfferForPackage = (pkg, offers) => {
  if (!pkg || !offers || offers.length === 0) return null;
  let best = null, bestAmt = 0;
  for (const o of offers) {
    if (o.promo_code) continue; // VIP-only offers don't auto-apply
    if (o.applies_to !== 'all' && o.applies_to !== pkg.id) continue;
    const amt = o.discount_type === 'percent'
      ? (Number(pkg.price || 0) * Number(o.discount_value || 0)) / 100
      : Math.min(Number(o.discount_value || 0), Number(pkg.price || 0));
    if (amt > bestAmt) { bestAmt = amt; best = { ...o, amount: Math.round(amt * 100) / 100 }; }
  }
  return best;
};

const PackagesSection = ({ onSelect, cms }) => {
  const [packages, setPackages] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const apiBase = process.env.REACT_APP_BACKEND_URL || '';
    let cancelled = false;
    const load = () => {
      Promise.all([
        fetch(`${apiBase}/api/packages`).then((r) => r.ok ? r.json() : []),
        fetch(`${apiBase}/api/special-offers/active`).then((r) => r.ok ? r.json() : []).catch(() => []),
      ]).then(([pkgs, offs]) => {
        if (cancelled) return;
        // Backend already filters by active=true on /api/packages. Defensive
        // belt-and-braces filter in case a future caller fetches all.
        setPackages((pkgs || []).filter((p) => p.active !== false));
        setOffers(offs || []);
        setLoading(false);
      });
    };
    load();
    // Live sync: admin edits a package photo / toggles card design in the CRM
    // tab → refetch here instantly (same-origin broadcast, no reload).
    const unsub = subscribeCmsUpdate(load);
    return () => { cancelled = true; unsub(); };
  }, []);

  // Card design chosen in Settings → Landing → Packages (default: compact).
  const cardDesign = cms?.card_design === 'full' ? 'full' : 'compact';

  // Section header copy comes from CMS (Settings → Landing Page Editor → Packages).
  const eyebrow = cms?.eyebrow || 'PACKAGES';
  const title = cms?.title || 'Pick a package — or build your own';
  const subtitle = cms?.subtitle || 'All packages are 120 minutes, include fruit-bouquet + bracelet/keychain stations, and add + HST on checkout.';
  const sectionBg = cms?.background_color || '#FFFDF8';
  const ctaText = cms?.footer_cta_text || 'Customize Your Own Package';

  return (
    <section id="packages" className="py-10 sm:py-14 fade-up" style={{ backgroundColor: sectionBg }}>
      <div className="container mx-auto px-4">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: `${BRAND.pink}15`, color: BRAND.pink }}>
            {eyebrow}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold mt-4 mb-3 text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            {title}
          </h2>
          <p className="text-[#6B6B7A] text-base sm:text-lg">
            {subtitle}
          </p>
        </div>

        {loading ? (
          <div className="text-center py-10 text-[#6B6B7A]">Loading packages…</div>
        ) : packages.length === 0 ? (
          <div className="text-center py-10 text-[#6B6B7A]">No packages available right now. Please check back soon.</div>
        ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 max-w-5xl mx-auto">
          {packages.map((pkg, i) => {
            const style = { ...DEFAULT_STYLE, ...(PACKAGE_STYLES[pkg.id] || {}) };
            const offer = bestOfferForPackage(pkg, offers);
            const offerLabel = offer
              ? (offer.discount_type === 'percent' ? `${offer.discount_value}% OFF` : `$${Number(offer.discount_value).toFixed(0)} OFF`)
              : null;
            const finalPrice = offer
              ? Math.max(0, Math.round((pkg.price - offer.amount) * 100) / 100)
              : pkg.price;
            return (
              <motion.button
                type="button"
                key={pkg.id}
                onClick={() => onSelect(pkg.id)}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06, duration: 0.3 }}
                viewport={{ once: true }}
                whileHover={{ y: -6, scale: 1.025 }}
                whileTap={{ scale: 0.985 }}
                className="group relative text-left rounded-3xl p-5 sm:p-6 border-2 transition-[box-shadow,border-color] duration-300 overflow-hidden cursor-pointer focus:outline-none focus-visible:ring-4"
                style={{
                  backgroundColor: style.cardBg,
                  borderColor: style.featured ? style.color : `${style.color}40`,
                  boxShadow: `0 6px 18px -10px ${style.glow}`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = `0 16px 38px -12px ${style.glow}, 0 0 0 2px ${style.color}`;
                  e.currentTarget.style.borderColor = style.color;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = `0 6px 18px -10px ${style.glow}`;
                  e.currentTarget.style.borderColor = style.featured ? style.color : `${style.color}40`;
                }}
                data-testid={`package-card-${pkg.id}`}
                aria-label={`Choose ${pkg.name}`}
              >
                {/* Shimmer sweep on hover */}
                <span
                  className="absolute -inset-y-2 -left-1/2 w-1/3 rotate-12 bg-gradient-to-r from-transparent via-white/60 to-transparent opacity-0 group-hover:opacity-100 group-hover:translate-x-[300%] transition-all duration-700 pointer-events-none"
                  aria-hidden
                />
                {/* Top accent stripe — wider on hover */}
                <span
                  className="absolute top-0 left-0 right-0 h-1 origin-left scale-x-30 group-hover:scale-x-100 transition-transform duration-300"
                  style={{ background: `linear-gradient(90deg, ${style.color}, ${style.color}88)` }}
                  aria-hidden
                />

                {/* Special offer badge (top-left) */}
                {offer && (
                  <span
                    className="absolute top-3 left-3 z-10 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full text-white shadow-md"
                    style={{ backgroundColor: BRAND.pink }}
                    data-testid={`landing-offer-badge-${pkg.id}`}
                    title={offer.title}
                  >
                    <Sparkles className="w-2.5 h-2.5" /> {offerLabel}
                  </span>
                )}
                {style.featured && (
                  <span
                    className="absolute top-3 right-3 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full text-white shadow-sm"
                    style={{ backgroundColor: style.color }}
                  >
                    Most Loved
                  </span>
                )}

                {/* Package photo (both designs) — placeholder if none set */}
                <div
                  className={`relative -mx-5 -mt-5 sm:-mx-6 sm:-mt-6 mb-3 overflow-hidden ${cardDesign === 'full' ? 'h-40' : 'h-32'}`}
                  style={{ backgroundColor: style.accent }}
                  data-testid={`package-image-${pkg.id}`}
                >
                  {pkg.image_url ? (
                    <img
                      src={cmsUrl(pkg.image_url)}
                      alt={pkg.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  ) : (
                    <div
                      className="w-full h-full flex items-center justify-center"
                      aria-hidden
                      data-testid={`package-image-placeholder-${pkg.id}`}
                    >
                      <span className="text-5xl opacity-80 transition-transform duration-300 group-hover:scale-110">{style.emoji}</span>
                    </div>
                  )}
                </div>

                <h3 className="text-lg sm:text-xl font-semibold text-[#1E1E24] leading-tight" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  {pkg.name}
                </h3>
                {style.tagline && (
                  <p className="text-[11px] font-medium mb-2" style={{ color: style.color }}>{style.tagline}</p>
                )}

                {/* Meta row — guests · duration */}
                <div className="flex items-center gap-3 text-[11px] text-[#6B6B7A] mb-3">
                  <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> Up to {pkg.guests || pkg.max_guests}</span>
                  <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {pkg.duration || '120 min'}</span>
                </div>

                {/* Price */}
                <p className="flex items-baseline gap-2 mb-3" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  <span className="text-3xl font-bold" style={{ color: offer ? BRAND.pink : style.color }} data-testid={`landing-price-${pkg.id}`}>
                    ${finalPrice}
                  </span>
                  {offer && (
                    <span className="text-sm text-[#6B6B7A] line-through" data-testid={`landing-price-strike-${pkg.id}`}>
                      ${pkg.price}
                    </span>
                  )}
                  <span className="text-[10px] font-semibold text-[#6B6B7A]">+ HST</span>
                </p>

                {/* Full Inclusions design — show every included item inline */}
                {cardDesign === 'full' && (pkg.features || []).length > 0 && (
                  <ul className="mb-3 space-y-1 text-left" data-testid={`package-inclusions-${pkg.id}`}>
                    {pkg.features.map((f, fi) => (
                      <li key={fi} className="flex items-start gap-1.5 text-xs text-[#4d4d57]">
                        <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-none" style={{ color: style.color }} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {/* CTA — full-width, plus (compact only) a discreet "View details" hint */}
                <div
                  className="w-full rounded-full text-white h-10 flex items-center justify-center font-semibold text-sm transition-all duration-200 group-hover:shadow-lg"
                  style={{ backgroundColor: style.color }}
                  data-testid={`package-select-${pkg.id}`}
                >
                  Choose
                  <ArrowRight className="w-4 h-4 ml-1.5 transition-transform group-hover:translate-x-1" />
                </div>
                {cardDesign === 'compact' && (
                  <p className="text-[10px] text-[#6B6B7A] text-center mt-2 opacity-70 group-hover:opacity-100 transition-opacity">
                    See full inclusions on the next page →
                  </p>
                )}
              </motion.button>
            );
          })}
        </div>
        )}

        <div className="text-center mt-12">
          <Button
            size="lg"
            onClick={() => onSelect()}
            className="rounded-full h-12 px-8 text-white font-semibold"
            style={{ background: `linear-gradient(135deg, ${BRAND.pink}, ${BRAND.orange})` }}
            data-testid="customize-own-package-btn"
          >
            {ctaText}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
    </section>
  );
};

// ---------- Section 5 · Testimonials ----------
const DEFAULT_TESTIMONIALS = [
  {
    quote: "My daughter's face when she made her own fruit bouquet was priceless. Every kid went home with a printed T-shirt they designed. Best. Party. Ever.",
    name: 'Sarah M.', role: 'mom of Emma, 7', stars: 5, bg: '#FFEFF5',
  },
  {
    quote: 'The bracelet station was a hit — even the shy kids joined in. So much better than pizza and games at another jump place.',
    name: 'David L.', role: 'dad of Arjun, 9', stars: 5, bg: '#EDFFF8',
  },
  {
    quote: 'We combined the cake studio with the craft corner. The hosts were incredible. My son is still wearing his printed cap a month later.',
    name: 'Priya K.', role: 'mom of Neil, 8', stars: 5, bg: '#FFF8E7',
  },
];

const TestimonialsSection = ({ cms }) => {
  const items = (cms?.items?.length ? cms.items : DEFAULT_TESTIMONIALS)
    // Hide rejected/pending entries on the public landing page; admins see them in the CMS.
    .filter((t) => t.approved !== false)
    .slice()
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  const eyebrow = cms?.eyebrow || 'PARENTS LOVE US';
  const title   = cms?.title   || 'Real kids. Real parties. Real smiles.';
  const designVariant = cms?.design_variant === 'film-strip' ? 'film-strip' : 'sticky-notes';

  // iter-104 — film-strip variant for testimonials. Reuses the same data,
  // just lays it out as a continuously-scrolling band.
  if (designVariant === 'film-strip') {
    const looped = [...items, ...items];
    return (
      <section className="py-10 sm:py-14 fade-up bg-white overflow-hidden" data-testid="testimonials-filmstrip">
        <div className="container mx-auto px-4 mb-8 text-center">
          <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: `${BRAND.teal}15`, color: BRAND.teal }}>
            {eyebrow}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold mt-4 text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            {title}
          </h2>
        </div>
        <div
          className="flex gap-6 hero-filmstrip-track hero-filmstrip-hoverable"
          style={{ animationDuration: '50000ms' }}
          data-testid="testimonials-filmstrip-track"
        >
          {looped.map((t, i) => (
            <blockquote
              key={`${t.id || i}-${i}`}
              className="flex-shrink-0 w-[300px] sm:w-[360px] rounded-3xl p-6 border border-[#1E1E24]/5 hero-filmstrip-card"
              style={{ backgroundColor: t.bg || '#FFEFF5' }}
            >
              <div className="flex gap-1 mb-3" aria-label={`${t.stars || 5} stars`}>
                {Array.from({ length: Number(t.stars || 5) }).map((_, s) => (
                  <Star key={s} className="w-4 h-4 fill-[#FFD166] text-[#FFD166]" />
                ))}
              </div>
              <p className="text-[#1E1E24] leading-relaxed mb-4 text-sm">&ldquo;{t.quote}&rdquo;</p>
              <footer className="text-sm flex items-center gap-3">
                {t.avatar_url && (
                  <img src={cmsUrl(t.avatar_url)} alt={t.name} className="w-8 h-8 rounded-full object-cover" />
                )}
                <span>
                  <span className="font-semibold text-[#1E1E24]">{t.name}</span>
                  {t.role && <span className="text-[#6B6B7A]"> · {t.role}</span>}
                </span>
              </footer>
            </blockquote>
          ))}
        </div>
      </section>
    );
  }

  // Default sticky-notes grid.
  return (
  <section className="py-10 sm:py-14 fade-up bg-white">
    <div className="container mx-auto px-4">
      <div className="max-w-2xl mx-auto text-center mb-12">
        <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: `${BRAND.teal}15`, color: BRAND.teal }}>
          {eyebrow}
        </span>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold mt-4 text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
          {title}
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {items.map((t, i) => (
          <motion.blockquote
            key={t.id || i}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.08 }}
            viewport={{ once: true }}
            className="rounded-3xl p-7 border border-[#1E1E24]/5"
            style={{ backgroundColor: t.bg || '#FFEFF5' }}
          >
            <div className="flex gap-1 mb-4" aria-label={`${t.stars || 5} stars`}>
              {Array.from({ length: Number(t.stars || 5) }).map((_, s) => (
                <Star key={s} className="w-4 h-4 fill-[#FFD166] text-[#FFD166]" />
              ))}
            </div>
            <p className="text-[#1E1E24] leading-relaxed mb-5">&ldquo;{t.quote}&rdquo;</p>
            <footer className="text-sm flex items-center gap-3">
              {t.avatar_url && (
                <img src={cmsUrl(t.avatar_url)} alt={t.name} className="w-8 h-8 rounded-full object-cover" />
              )}
              <span>
                <span className="font-semibold text-[#1E1E24]">{t.name}</span>
                {t.role && <span className="text-[#6B6B7A]"> · {t.role}</span>}
              </span>
            </footer>
          </motion.blockquote>
        ))}
      </div>
    </div>
  </section>
  );
};

// ---------- Section 6 · FAQ ----------
const FAQS = [
  { q: 'Can you really print on caps and T‑shirts during the party?', a: 'Yes — we bring a portable printing press. Kids draw or choose a design and we print it right there. Each guest leaves with their own custom souvenir.' },
  { q: 'Do you provide all the craft materials?', a: 'Absolutely everything is included — beads, paints, glue, glitter, safety scissors, aprons. Just show up.' },
  { q: 'Can we combine fruit bouquet making with other activities?', a: 'Yes. Our Custom Package Builder lets you mix any combination of stations, food and decor to build exactly the party you want.' },
  { q: 'How do custom cakes work?', a: 'You choose flavour, filling and theme (unicorn, superhero, dinosaur — anything). Our baker makes and decorates it fresh for your date.' },
  { q: 'What about allergies?', a: 'We use separate stations for allergen‑sensitive foods and can accommodate most allergies with advance notice. Tell us at booking and we will plan around it.' },
  { q: 'Where are you located?', a: 'We are located inside Edible Arrangements Oakville at 1500 Upper Middle Rd W, Unit 4, Oakville, ON L6M 3G3. Look for the Edible Arrangements storefront — our studio is right inside!' },
];

const FAQSection = ({ cms }) => {
  if (cms && cms.visible === false) return null;
  const eyebrow = (cms && cms.eyebrow !== undefined) ? cms.eyebrow : 'FAQ';
  const title = cms?.section_title || 'Quick answers';
  const list = (Array.isArray(cms?.items) && cms.items.length)
    ? cms.items.filter((f) => f.active !== false).map((f) => ({ q: f.question, a: f.answer }))
    : FAQS;
  if (!list.length) return null;
  return (
    <section className="py-10 sm:py-14 fade-up" style={{ backgroundColor: '#F0F0FF' }} data-testid="faq-section">
      <div className="container mx-auto px-4 max-w-3xl">
        <div className="text-center mb-10">
          {eyebrow ? (
            <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: `${BRAND.indigo}15`, color: BRAND.indigo }}>
              {eyebrow}
            </span>
          ) : null}
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold mt-4 text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            {title}
          </h2>
        </div>
        <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
          {list.map((f, i) => (
            <AccordionItem
              key={i}
              value={`item-${i}`}
              className="bg-white rounded-2xl border border-[#1E1E24]/5 px-5"
            >
              <AccordionTrigger className="text-left text-[#1E1E24] font-semibold hover:no-underline" data-testid={`faq-q-${i}`}>
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-[#6B6B7A] leading-relaxed">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

// ---------- Section 7 · Sticky CTA Strip ----------
const StickyCTA = ({ visible, onClick }) => (
  <div
    className={`fixed bottom-0 left-0 right-0 z-40 transition-transform duration-300 ${visible ? 'translate-y-0' : 'translate-y-full'}`}
    data-testid="sticky-cta"
  >
    <div className="container mx-auto px-4 pb-4">
      <div
        className="rounded-2xl shadow-2xl px-5 py-3 sm:px-6 sm:py-4 flex items-center justify-between gap-4 text-white"
        style={{ background: `linear-gradient(135deg, ${BRAND.pink}, ${BRAND.orange})` }}
      >
        <p className="text-sm sm:text-base font-semibold">
          <span className="hidden sm:inline">Ready to create unforgettable memories? </span>
          <span className="sm:hidden">Ready to party?</span>
        </p>
        <Button
          onClick={onClick}
          className="bg-white text-[#FF3B6B] hover:bg-white/90 rounded-full h-10 px-5 font-semibold"
          data-testid="sticky-cta-btn"
        >
          Start Your Party Plan
          <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </div>
    </div>
  </div>
);

// ---------- Footer ----------
const SOCIAL_ICONS = {
  facebook: { url_field: 'facebook', label: 'Facebook' },
  instagram: { url_field: 'instagram', label: 'Instagram' },
  tiktok: { url_field: 'tiktok', label: 'TikTok' },
  youtube: { url_field: 'youtube', label: 'YouTube' },
  twitter: { url_field: 'twitter', label: 'X / Twitter' },
};

const SiteFooter = ({ cms }) => {
  const company = cms?.company_name || 'Creative Birthdays';
  const tagline = cms?.tagline || 'Hands-on parties for kids — fruit bouquets, crafts, custom cakes and print-your-own gifts.';
  const phone = cms?.phone || '905-616-7617';
  const phoneTel = cms?.phone_tel || '+19056167617';
  const email = cms?.email || 'info@creativebirthdays.ca';
  const addrLines = (cms?.address_lines && cms.address_lines.length)
    ? cms.address_lines
    : ['Inside Edible Arrangements', '1500 Upper Middle Rd W, Unit 4', 'Oakville, Ontario L6M 3G3'];
  const exploreLinks = (cms?.explore_links && cms.explore_links.length) ? cms.explore_links : [
    { label: 'Book a Party', href: '/book' },
    { label: 'Contact Us', href: '/contact' },
    { label: 'Careers', href: '/careers' },
    { label: 'Login', href: '/login' },
    { label: 'Edible Arrangements Oakville ↗', href: 'https://www.ediblearrangements.ca/stores/ca/on/oakville' },
  ];
  const social = cms?.social_links || {};
  const copyright = (cms?.copyright_text || '© {year} {company}. All rights reserved.')
    .replace('{year}', new Date().getFullYear())
    .replace('{company}', company);
  return (
  <footer className="py-10 bg-[#1E1E24] text-white" data-testid="site-footer">
    <div className="container mx-auto px-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
        <div>
          <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Fredoka, sans-serif', color: BRAND.pink }}>
            {company}
          </h3>
          <p className="text-white/60 text-sm leading-relaxed">
            {tagline}
          </p>
          {Object.entries(social).some(([, v]) => v) && (
            <div className="flex gap-3 mt-4">
              {Object.entries(SOCIAL_ICONS).map(([key, meta]) =>
                social[key] ? (
                  <a
                    key={key}
                    href={social[key]}
                    target="_blank"
                    rel="noreferrer"
                    className="text-white/60 hover:text-white text-sm font-medium"
                    data-testid={`footer-social-${key}`}
                  >
                    {meta.label}
                  </a>
                ) : null
              )}
            </div>
          )}
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-white/40 mb-3">Get in touch</p>
          <ul className="space-y-2 text-sm">
            {phone && <li className="flex items-center gap-2"><Phone className="w-4 h-4 flex-shrink-0" /> <a href={`tel:${phoneTel}`} className="hover:text-white text-white/80">{phone}</a></li>}
            {email && <li className="flex items-center gap-2"><Mail className="w-4 h-4 flex-shrink-0" /> <a href={`mailto:${email}`} className="hover:text-white text-white/80" data-testid="footer-email">{email}</a></li>}
            <li className="flex items-start gap-2"><MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span className="text-white/80 leading-relaxed" data-testid="footer-address">
                {addrLines.map((l, i) => (
                  <React.Fragment key={i}>{l}{i < addrLines.length - 1 && <br/>}</React.Fragment>
                ))}
              </span>
            </li>
          </ul>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-white/40 mb-3">Explore</p>
          <ul className="space-y-2 text-sm">
            {exploreLinks.map((l) => {
              const isExternal = /^https?:\/\//.test(l.href || '');
              return isExternal
                ? <li key={l.href}><a href={l.href} target="_blank" rel="noreferrer" className="text-white/80 hover:text-white">{l.label}</a></li>
                : <li key={l.href}><Link to={l.href} className="text-white/80 hover:text-white" data-testid={`footer-link-${(l.label||'').toLowerCase().replace(/\s+/g,'-')}`}>{l.label}</Link></li>;
            })}
          </ul>
        </div>
      </div>
      <div className="pt-8 border-t border-white/10 text-center text-white/40 text-xs">
        {copyright}
      </div>
    </div>
  </footer>
  );
};

// ---------- Page ----------
export const Landing = () => {
  const navigate = useNavigate();
  const [stickyVisible, setStickyVisible] = useState(false);
  const [availOpen, setAvailOpen] = useState(false);
  // Single fetch for all CMS-managed copy (cached for 60s).
  const cms = useCmsContent();

  useEffect(() => {
    const onScroll = () => setStickyVisible(window.scrollY > 600);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Support /#packages hash deep-linking from other pages
  useEffect(() => {
    if (window.location.hash === '#packages') {
      const t = setTimeout(() => {
        const el = document.getElementById('packages');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
      return () => clearTimeout(t);
    }
  }, []);

  // Fade-up-on-scroll: toggle `is-visible` when `.fade-up` elements enter viewport
  useEffect(() => {
    const nodes = document.querySelectorAll('.fade-up');
    if (!nodes.length) return;
    // Respect prefers-reduced-motion (CSS already neutralises transform; also skip observer)
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      nodes.forEach((n) => n.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('is-visible');
          io.unobserve(e.target);
        }
      });
    }, { rootMargin: '0px 0px -5% 0px', threshold: 0 });
    nodes.forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, []);

  const scrollToPackages = () => {
    const el = document.getElementById('packages');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const goBook = (packageId) => {
    if (packageId) navigate(`/book?package=${packageId}`);
    else navigate('/book');
  };

  const handleLearnMore = (exp) => {
    // If the experience has a dedicated detail page, go there.
    if (exp?.href) {
      navigate(exp.href);
      return;
    }
    // Otherwise, each experience is included in packages — scroll to them.
    scrollToPackages();
  };

  return (
    <div className="min-h-screen bg-[#FFFDF8]">
      <Header />
      <ReturningProspectBanner onViewPackages={scrollToPackages} />
      {(() => {
        const variant = cms?.hero?.variant || 'flash';
        const heroProps = {
          cms: cms?.hero || {},
          onPrimary: scrollToPackages,
          onOpenAvailability: () => setAvailOpen(true),
        };
        switch (variant) {
          case 'celebration':  return <HeroCelebrationVariant {...heroProps} />;
          case 'split':        return <HeroSplit {...heroProps} />;
          case 'mascot':       return <HeroMascot {...heroProps} />;
          case 'video':        return <HeroVideo {...heroProps} />;
          case 'minimalist':   return <HeroMinimalist {...heroProps} />;
          case 'story_cards':  return <HeroStoryCards {...heroProps} />;
          default:             return <HeroSection {...heroProps} />;
        }
      })()}
      {/* iter-104: optional film-strip slides band, toggleable from the CMS */}
      {cms?.hero_slides?.enabled && <HeroFilmStripSlides config={cms.hero_slides} />}
      <HeroHighlightBanner />
      <ExperiencesSection onLearnMore={handleLearnMore} cms={cms?.experiences} />
      <HowItWorksSection cms={cms?.how_it_works} />
      <PackagesSection onSelect={goBook} cms={cms?.packages_section} />
      <TestimonialsSection cms={cms?.testimonials} />
      <FAQSection cms={cms?.faq} />
      <SiteFooter cms={cms?.footer} />
      <StickyCTA visible={stickyVisible} onClick={scrollToPackages} />

      <Dialog open={availOpen} onOpenChange={setAvailOpen}>
        <DialogContent className="bg-white max-w-md p-5 sm:p-6" data-testid="availability-modal">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Fredoka' }} className="flex items-center gap-2 text-xl">
              <CalendarDays className="w-5 h-5 text-[#FF3B6B]" /> Check availability
            </DialogTitle>
            <DialogDescription>Pick an available date — we'll jump you into booking with the date pre-selected.</DialogDescription>
          </DialogHeader>
          <AvailabilityCalendar compact onPicked={() => setAvailOpen(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Landing;
